import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import API, { setAuthTokens, clearAuthTokens } from '../services/api';

const AuthContext = createContext({
  user: null,
  company: null,
  permissions: [],
  isSuperuser: false,
  loading: true,
  isAuthenticated: false,
  authVersion: 0,
  refreshAuth: () => {},
  logout: () => {},
  signinAndBootstrap: async () => {},
  hardResetAuth: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [authVersion, setAuthVersion] = useState(0);
  const bcRef = useRef(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(null); setCompany(null); setPermissions([]); setIsSuperuser(false);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data } = await API.get('company/access/me_context/');
      console.log('AuthProvider load data:', data);
      setUser(data?.user || null);
      const baseCompany = data?.company || null;
      setCompany(baseCompany);
      const perms = Array.isArray(data?.permissions) ? data.permissions : [];
      setPermissions(perms);
      setIsSuperuser(Boolean(perms.includes('__superuser__') || data?.user?.is_superuser));

    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        // Sin acceso asignado pero tokens válidos
        setUser(null); setCompany(null); setPermissions([]); setIsSuperuser(false);
      } else {
        // Token inválido/expirado
        try { localStorage.removeItem('access_token'); localStorage.removeItem('refresh_token'); } catch {}
        setUser(null); setCompany(null); setPermissions([]); setIsSuperuser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAuth = useCallback(() => { load(); }, [load]);

  const hardResetAuth = useCallback(() => {
    clearAuthTokens();                      // quita Authorization del axios y localStorage
    setUser(null);
    setCompany(null);
    setPermissions([]);
    setIsSuperuser(false);
    setLoading(false);
    setAuthVersion(v => v + 1);             // 🔑 fuerza remount en árboles con key={authVersion}
    try { bcRef.current?.postMessage('logout'); } catch {}
  }, []);

  const logout = useCallback(() => {
    hardResetAuth();
  }, [hardResetAuth]);

  const signinAndBootstrap = useCallback(async ({ access, refresh }) => {
    setLoading(true);
    setAuthTokens({ access, refresh });
    await load();
    setAuthVersion(v => v + 1);             // 🔑 remount post-login
    try { bcRef.current?.postMessage('login'); } catch {}
  }, [load]);

  useEffect(() => { load(); }, [load]);

  // Sync entre pestañas
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'access_token' || e.key === 'refresh_token') {
        load();
        setAuthVersion(v => v + 1);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [load]);

  useEffect(() => {
    try {
      bcRef.current = new BroadcastChannel('auth');
      bcRef.current.onmessage = (e) => {
        if (e?.data === 'logout' || e?.data === 'login') {
          load();
          setAuthVersion(v => v + 1);
        }
      };
    } catch {}
    return () => { try { bcRef.current?.close(); } catch {} };
  }, [load]);

  const isAuthenticated = Boolean(localStorage.getItem('access_token'));

  const value = useMemo(() => ({
    user, company, permissions, isSuperuser,
    loading, isAuthenticated, authVersion,
    refreshAuth, logout, signinAndBootstrap, hardResetAuth
  }), [user, company, permissions, isSuperuser, loading, isAuthenticated, authVersion, refreshAuth, logout, signinAndBootstrap, hardResetAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
